import React, { useEffect, useRef, useState, useCallback } from 'react';
import { zIndexManager } from '@/utils/z-index-manager';

/**
 * 用于管理窗口 z-index 的 Hook
 * @param windowId 窗口唯一标识，如果不提供则自动生成
 * @param autoBringToFront 是否在点击时自动提升到最前，默认为 true
 * @returns { zIndex, bringToFront, windowId, handleClick }
 */
export const useZIndex = (windowId?: string, autoBringToFront = true) => {
    const idRef = useRef<string>(windowId || `window-${Date.now()}-${Math.random()}`);
    const [zIndex, setZIndex] = useState<number>(() => {
        return zIndexManager.registerWindow(idRef.current);
    });
    // 提升到最前
    const bringToFront = useCallback(() => {
        const newZIndex = zIndexManager.bringToFront(idRef.current);
        setZIndex(newZIndex);
    }, []);

    // 处理点击事件，自动提升到最前
    const handleClick = useCallback(() => {
        if (autoBringToFront) {
            bringToFront();
        }
    }, [autoBringToFront, bringToFront]);

    // 组件挂载时注册，卸载时注销
    useEffect(() => {
        const id = idRef.current;
        // 如果提供了 windowId 且与当前不同，重新注册
        if (windowId && windowId !== id) {
            zIndexManager.unregisterWindow(id);
            idRef.current = windowId;
            const newZIndex = zIndexManager.registerWindow(windowId);
            setZIndex(newZIndex);
        }

        return () => {
            zIndexManager.unregisterWindow(idRef.current);
        };
    }, [windowId]);

    return {
        zIndex,
        bringToFront,
        windowId: idRef.current,
        handleClick,
    };
};
